/**
 * Step 1 — your money today (cashflow rebuild, 21 Oct 2026; the agreed mock-up's j1-step1 and j2-step1).
 *
 * THE SUPPORT COST FIRST, because it changes the numbers (review, 18 Oct 2026: it was asked after the four
 * cards, which then changed under the reader). It is the client's own figure from the provider's invoices: we
 * can't work it out, and it is never written to the file. Then the four numbers, each with its "why?": the Age
 * Pension, what is left over (or short) each year, what they own, and what is left to the family after about
 * ten years — with "why 10?" and the one control for the years (the review: "Estate in [N] years" and "Project
 * over" were two controls for one setting).
 *
 * A returning client who changes the support cost is told the plans are re-tested, and that the chosen plan
 * was priced on the old figure and stays chosen until they choose again (j2-step1).
 */
import React from 'react';
import { ArrowRight } from 'lucide-react';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { ProjectionYearsInput } from '../../components/ui/ProjectionYearsInput.tsx';
import { householdVoice } from '../../utils/householdWords.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { PositionSummary, SupportCostInput } from './cashflowPensionScenario.ts';
import type { CpPlanCardRecord } from './cpChosenPlan.ts';
import { supportChangedSince } from './cpChosenPlan.ts';
import { Tile, money, whyLink, card, sec, WHY_TEN_YEARS } from './cpUi.tsx';

export interface CpStep1Props {
    factFind: DraftFactFind;
    supportAnnual: number | '';
    onSupportAnnual: (v: number | '') => void;
    supportLabel: string;
    onSupportLabel: (v: string) => void;
    supportCost: SupportCostInput | null;
    position: PositionSummary | null;
    loading: boolean;
    error: string | null;
    legacyYears: number;
    onLegacyYears: (y: number) => void;
    /** The chosen plan's card, to say when it was priced on a different support cost. */
    chosenCard: CpPlanCardRecord | null;
    /** "why?" on a number: step 2, where each is explained. */
    onWhy: () => void;
    onNext: () => void;
}

const CpStep1 = ({ factFind, supportAnnual, onSupportAnnual, supportLabel, onSupportLabel, supportCost, position, loading, error, legacyYears, onLegacyYears, chosenCard, onWhy, onNext }: CpStep1Props) => {
    const v = householdVoice(factFind);
    const [whyTen, setWhyTen] = React.useState(false);
    const current = supportCost?.annual ?? 0;
    const changed = supportChangedSince(chosenCard, current);
    return (
        <div className="space-y-5" data-testid="cp-step1">
            <div className={card} data-testid="cp-support-cost">
                <p className={sec}>{v.you ? 'Do you already pay for help at home?' : `${v.couple ? 'Do' : 'Does'} ${v.names} already pay for help at home?`}</p>
                <p className="mt-1 text-[15px] leading-relaxed text-slate-700">
                    Cleaning, meals, transport, personal care: if {v.names} pay{v.s} for any of it, put the yearly cost here. <strong>We can&apos;t work this out for {v.them}</strong>: these fees are set by each provider, so the figure comes from {v.their} provider or {v.their} invoices. Leave it empty if there is none.
                </p>
                <div className="mt-3 flex flex-wrap items-end gap-3">
                    <div className="w-48">
                        <CurrencyInput label="Yearly cost" value={supportAnnual} onChange={(x) => onSupportAnnual(x ?? '')} className="" />
                    </div>
                    <span className="pb-2.5 text-[14px] text-slate-500">a year, for</span>
                    <div className="min-w-[14rem] flex-1 sm:max-w-sm">
                        <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor="cp-support-label">What it is for</label>
                        <input
                            id="cp-support-label"
                            type="text"
                            value={supportLabel}
                            onChange={(e) => onSupportLabel(e.target.value)}
                            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-blue focus:ring-2 focus:ring-brand-blue"
                        />
                    </div>
                </div>
                {supportCost && (
                    <p className="mt-3 text-[14px] leading-relaxed text-slate-700">
                        <strong>{v.you ? 'You told us' : 'We were told'}</strong> {supportLabel.toLowerCase()} costs {money(supportCost.annual)} a year. The numbers below include it.{' '}
                        Paying for support does <strong>not</strong> change {v.possessive} Age Pension — that is assessed on what {v.they} own and earn, not on what {v.they} spend. It changes only what is left over each year.
                    </p>
                )}
            </div>

            {changed && chosenCard && (
                <div className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700" data-testid="cp-support-changed">
                    The four numbers and the plans are re-tested with {money(current)} a year of support. {v.Possessive} chosen plan was priced on {money(chosenCard.support.annual)}, and stays chosen until {v.they} choose again on step 3; the advice document will use the new cost once {v.they} do.
                </div>
            )}

            {error && (
                <div className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] text-slate-700">
                    <strong className="text-slate-900">We couldn&apos;t work out {v.possessive} position.</strong> {error} This usually means something in {v.possessive} profile is incomplete: check {v.their} assets and income, then come back.
                </div>
            )}
            {loading && !position && (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-6 text-center text-[14px] text-slate-600">Working out {v.possessive} position…</div>
            )}

            {position && (
                <>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="cp-four-numbers">
                        <Tile label="Age Pension" value={money(position.agePension)} unit="a year" caption={<>{v.couple ? (v.you ? 'for the two of you' : 'for the two of them') : 'a year'} · <button type="button" onClick={onWhy} className={whyLink}>why?</button></>} />
                        <Tile
                            label={position.cashFlow < 0 ? 'Each year, short by' : 'Left over each year'}
                            value={money(Math.abs(position.cashFlow))}
                            tone={position.cashFlow < 0 ? 'bad' : 'good'}
                            caption={<>{supportCost ? `after the ${money(supportCost.annual)} of support` : 'money in, less money out'} · <button type="button" onClick={onWhy} className={whyLink}>why?</button></>}
                        />
                        <Tile label={`What ${v.they} own`} value={money(position.netWorth)} caption={`less what ${v.they} owe, today`} />
                        <Tile
                            label="Left to the family"
                            value={money(position.estate)}
                            testId="cp-estate-tile"
                            caption={<><ProjectionYearsInput years={legacyYears} onChange={onLegacyYears} before="after about" /> · <button type="button" onClick={() => setWhyTen((o) => !o)} aria-expanded={whyTen} className={whyLink} data-testid="cp-why-ten">why {legacyYears}?</button></>}
                        />
                    </div>
                    {whyTen && <p className="rounded-xl border border-slate-200 border-l-4 border-l-blue-500 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700" data-testid="cp-why-ten-words">{WHY_TEN_YEARS}</p>}
                    {position.shortfallYear != null && (
                        <p className="rounded-xl border border-slate-200 border-l-4 border-l-amber-400 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700" data-testid="cp-runs-short">
                            <strong className="text-slate-900">On these figures the savings can no longer cover the gap from about year {position.shortfallYear}.</strong> Not a prediction, but the point worth planning around. The next step shows where the money comes from until then.
                        </p>
                    )}
                </>
            )}

            <div className="flex flex-wrap items-center justify-end gap-3">
                <button type="button" onClick={onNext} disabled={!position} data-testid="cp-step1-next"
                    className="inline-flex h-11 items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 px-5 text-[14px] font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50">
                    Why the numbers are what they are <ArrowRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default CpStep1;
